/**
 * KillFeed — HUD element (extracted from PixiHUDRenderer.js in Phase 3D).
 *
 * create(hud) builds the PIXI objects into hud.elements.killFeed,
 * position(hud) lays them out for hud.screenWidth/Height, update(hud, …) redraws
 * from game state. `hud` is the PixiHUDRenderer coordinator: it owns
 * hudContainer, elements, screen size, _createText() and send().
 */
import * as PIXI from 'pixi.js';

/**
 * Create kill feed
 */
export function create(hud) {
  const container = new PIXI.Container();
  hud.hudContainer.addChild(container);

  // Pre-create feed entry containers (max 5 visible)
  const entries = [];
  for (let i = 0; i < 5; i++) {
    const entryContainer = new PIXI.Container();
    entryContainer.visible = false;
    container.addChild(entryContainer);

    const background = new PIXI.Graphics();
    entryContainer.addChild(background);

    const killerText = hud._createText('', 16, 0xff5050);
    killerText.anchor.set(0, 0.5);
    entryContainer.addChild(killerText);

    const middleText = hud._createText(' eliminated ', 16, 0xffffff);
    middleText.anchor.set(0, 0.5);
    entryContainer.addChild(middleText);

    const victimText = hud._createText('', 16, 0xff5050);
    victimText.anchor.set(0, 0.5);
    entryContainer.addChild(victimText);

    entries.push({ container: entryContainer, background, killerText, middleText, victimText });
  }

  hud.elements.killFeed = { container, entries };
}

/**
 * Update kill feed
 * @param {Array} eliminationFeed - Feed entries
 * @param {string} localId - Local player ID
 * @param {Object} callbacks - Callback functions
 */
export function update(hud, eliminationFeed, localId, callbacks) {
  const kf = hud.elements.killFeed;
  if (!kf) return;

  const { uiConfig } = callbacks;
  const now = performance.now();
  const fadeOutDuration = uiConfig?.eliminationFeed?.fadeOutDuration || 500;

  // Top-left corner. The minimap moved to the right side, so the feed gets the
  // corner outright; the L leaderboard popup draws over it when opened.
  const startX = 30;
  const startY = 30;
  const lineHeight = 35;
  const padding = 10;

  kf.entries.forEach((entry, index) => {
    if (!eliminationFeed || index >= eliminationFeed.length) {
      entry.container.visible = false;
      return;
    }

    const msg = eliminationFeed[index];
    entry.container.visible = true;

    // Calculate opacity
    let opacity = 1.0;
    if (index === 0 && msg.expiresAt) {
      const timeUntilExpire = msg.expiresAt - now;
      if (timeUntilExpire < fadeOutDuration) {
        opacity = Math.max(0, timeUntilExpire / fadeOutDuration);
      }
    }
    entry.container.alpha = opacity;

    // Position
    entry.container.x = startX;
    entry.container.y = startY + index * lineHeight;

    // Build text with colors (simplified - single color for now)
    const killerText = msg.killerName || msg.killer;
    const victimText = msg.victimName || msg.victim;

    entry.killerText.text = killerText;
    entry.middleText.text = ' eliminated ';
    entry.victimText.text = victimText;

    entry.killerText.x = padding;
    entry.killerText.y = 15;

    entry.middleText.x = entry.killerText.x + entry.killerText.width;
    entry.middleText.y = 15;

    entry.victimText.x = entry.middleText.x + entry.middleText.width;
    entry.victimText.y = 15;

    entry.killerText.style.fill = 0xff5050;
    entry.middleText.style.fill = 0xffffff;
    entry.victimText.style.fill = 0xff5050;

    const totalWidth = (entry.victimText.x + entry.victimText.width) - padding;
    entry.background.clear();
    entry.background.rect(0, 0, totalWidth + padding * 2, 30);
    entry.background.fill({ color: 0x000000, alpha: 0.35 * opacity });
  });
}
